import { ModalCustom } from "@/components/Common"
import { Stepper, StepperNavigation } from "@/components/ui/stepper"
import { useClientsQuery } from "@/query/clientQuery"
import {
  CollectionState,
  nextWithoutAppointmentStep,
  previousWithoutAppointmentStep,
  setCollectionInfo,
  setSelectedClientId,
  setWithoutAppointmentStep,
} from "@/store/cashRegisterReducers/cashRegisterSlice"
import { setOpenModalCashRegisterWithoutAppointment } from "@/store/modalsReducers/modalSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { CreditCard, FileCheck, Package } from "lucide-react"
import React, { useEffect } from "react"
import { UnifiedPaymentStep } from "../commons/unifiedPayment/UnifiedPaymentStep"
import { ValidateServiceStep } from "../commons/validateService/ValidateServiceStep"
// Retirer les imports non nécessaires
import { Button, Select } from "@okeys/shared-react-components"

// Définition des étapes pour le parcours sans rendez-vous
const STEPS = [
  {
    id: "validateService",
    title: "Sélection Service",
    icon: Package,
  },
  {
    id: "validatePayment",
    title: "Paiement",
    icon: CreditCard,
  },
  {
    id: "validate",
    title: "Finalisation",
    icon: FileCheck,
  },
]

export const CashRegisterWithoutAppointmentModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const { openModalCashRegisterWithoutAppointment } = useAppSelector(
    (state) => state.modal
  )
  const { collectionInfo } = useAppSelector((state) => state.cashRegister)

  // Synchronisation retirée - l'état reste uniquement dans Redux

  // Initialiser l'état si nécessaire
  useEffect(() => {
    if (openModalCashRegisterWithoutAppointment.isOpen && !collectionInfo) {
      dispatch(
        setCollectionInfo({
          collectionState: "validateService",
          services: [],
          serviceWithPromo: [],
          discountAppliedGlobal: null,
          keysUsed: [],
          paymentInfo: {
            method: "cash",
          },
        })
      )
    }
  }, [openModalCashRegisterWithoutAppointment.isOpen, collectionInfo, dispatch])

  const handleClose = () => {
    dispatch(setOpenModalCashRegisterWithoutAppointment({ isOpen: false }))
  }

  const handleStepChange = (stepId: string) => {
    dispatch(setWithoutAppointmentStep(stepId as CollectionState))
  }

  const handleNext = () => {
    dispatch(nextWithoutAppointmentStep())
  }

  const handlePrevious = () => {
    dispatch(previousWithoutAppointmentStep())
  }

  const handleFinish = () => {
    // Logique de finalisation à implémenter plus tard
    handleClose()
  }

  // Déplacer les hooks avant l'early return
  // Après const { collectionInfo } = useAppSelector(...)
  const { clients, isLoading } = useClientsQuery()
  const selectedClient = collectionInfo?.selectedClientId

  const clientOptions = React.useMemo(() => {
    return clients.map((client: { clientId: string; email: string }) => ({
      value: client.clientId,
      label: client.email,
    }))
  }, [clients])

  // Vérification early return AVANT tout calcul
  if (!collectionInfo) {
    return null
  }

  // Maintenant on peut calculer en sécurité
  const currentStep = collectionInfo.collectionState
  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep)
  const currentStepData = STEPS[currentStepIndex]

  // Protection supplémentaire au cas où currentStep ne serait pas dans STEPS
  if (!currentStepData) {
    console.error("Step not found:", currentStep)
    return null
  }

  const canGoBack = currentStepIndex > 0
  const canGoNext =
    currentStepIndex < STEPS.length - 1 &&
    (currentStep !== "validateService" ||
      (collectionInfo.services?.length || 0) > 0)
  const isLastStep = currentStepIndex === STEPS.length - 1

  // Rendu du contenu de l'étape actuelle
  const renderStepContent = () => {
    const IconComponent = currentStepData.icon

    switch (currentStep) {
      case "validateService":
        return (
          <div className="h-full">
            <ValidateServiceStep />
          </div>
        )

      case "validatePayment":
        return (
          <div className="h-full">
            <UnifiedPaymentStep />
          </div>
        )

      case "validate":
        return (
          <div className="text-center">
            <IconComponent className="h-16 w-16 mx-auto mb-4 text-green-600" />
            <h3 className="text-lg font-semibold mb-2">Finalisation</h3>
            <p className="text-gray-600 mb-4">
              Terminer l'encaissement et imprimer le reçu
            </p>
            {collectionInfo.services && (
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-green-700 mb-1">
                  Services sélectionnés : {collectionInfo.services.length}
                </p>
                <p className="text-sm text-green-700">
                  Montant total :{" "}
                  {collectionInfo.paymentProgress?.totalAmount?.toFixed(2)}€
                </p>
              </div>
            )}
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">
                Prêt à finaliser l'encaissement
              </p>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <ModalCustom
      isOpen={openModalCashRegisterWithoutAppointment.isOpen}
      onClose={handleClose}
      maxWidth={95}
      maxHeight={95}
    >
      <div className="flex flex-col flex-1 bg-background border border-border rounded-lg overflow-hidden">
        {/* Header personnalisé */}
        <div className="flex items-center justify-between mb-6 border-b border-border bg-background p-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Encaissement Sans Rendez-vous
              </h2>
              <p className="text-sm text-muted-foreground">
                Étape {currentStepIndex + 1} sur {STEPS.length} -{" "}
                {currentStepData.title}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Select
              options={clientOptions}
              value={selectedClient}
              onChange={(value) => {
                dispatch(setSelectedClientId(value))
              }}
              placeholder="Sélectionner client..."
              disabled={isLoading || clients.length === 0}
              className="w-[300px]"
              searchable={true}
              searchPlaceholder="Rechercher un client..."
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              ✕
            </Button>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-6 mb-6">
          <Stepper
            steps={STEPS.map((step) => ({
              id: step.id,
              title: step.title,
              icon: step.icon,
            }))}
            currentStep={currentStep}
            onStepChange={handleStepChange}
          />
        </div>

        {/* Contenu de l'étape */}
        <div className="flex-1 px-6 pb-6">{renderStepContent()}</div>

        {/* Navigation */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-2">
          <StepperNavigation
            canGoBack={canGoBack}
            canGoNext={canGoNext}
            isLastStep={isLastStep}
            onBack={handlePrevious}
            onNext={handleNext}
            onFinish={handleFinish}
            nextLabel="Suivant"
            backLabel="Précédent"
            finishLabel="Finaliser"
          />
        </div>
      </div>
    </ModalCustom>
  )
}
